import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getRecruitJobs, getRecruitJobsMetaData, postRecruit } from '../apis';
import { FormattedMessage, useIntl } from 'react-intl';
import { yamazaki_brown } from '../styles/color';
import { appGetLocale, appJobNatureDict, appPathPrefix, isMobileAspectRatio } from '../helpers/appHelper';

const JobsApplication = () => {
    const params = useParams();
    const location = useLocation();
    const nav = useNavigate();
    const intl = useIntl();
    const [jobData, setJobData] = useState(null);
    const [jobMetaData, setJobMetaData] = useState(null);
    const job_nature_dict = appJobNatureDict();
    const appLanguage = appGetLocale();
    const prefix = appPathPrefix();
    const [firstArea, setFirstArea] = useState(null);
    const [firstDistrict, setFirstDistrict] = useState(null);
    const isMobile = isMobileAspectRatio();
    const isEng = appLanguage === 'en';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const job = location?.state?.job;
        const nature = params.jobsNature;

        if (job) {
            setJobData(job);
            setFirstArea(job.job_locations[0].city_name);
            setFirstDistrict(job.job_locations[0].districts[0].district_code);
            getMetaData();
        }
        else if (nature !== 'baker' && nature !== 'sales' && nature !== 'logistics' && nature !== 'factory' && nature !== 'office') {
            nav(prefix + '/')
        }
        else {
            getJobData();
            getMetaData();
        }
    }, [location]);

    useEffect(() => {
        getJobData();
        getMetaData();
    }, [appLanguage]);

    const getJobData = async () => {
        const result = await getRecruitJobs({ job_nature: job_nature_dict[params.jobsNature] });

        if (!result.fail) {
            const job = result.find(job => job.id == params.jobsId);
            if (job) {
                setJobData(job);
                setFirstArea(job.job_locations[0].city_name);
                setFirstDistrict(job.job_locations[0].districts[0].district_code);
            } else {
                nav(prefix + '/careers/' + params.jobsNature);
            }
        }
    };

    const getMetaData = async () => {
        const metaData = await getRecruitJobsMetaData();
        if (!metaData.fail) {
            setJobMetaData(metaData);
        } else {
            nav(prefix + '/careers/' + params.jobsNature);
        }
    };

    const NavBar = () => (
        isMobile ?
        <div className="jobs-nav">
            <a href={prefix + "/"} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    <FormattedMessage id={'menus.home'} />
                </span>
            </a>
            <span> {">"} </span>
            <a href={prefix + "/careers"} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    <FormattedMessage id={'menus.recruit'} />
                </span>
            </a>
            <span> {">"} </span>
            <a href={prefix + "/careers/" + params.jobsNature} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    {
                        params.jobsNature === 'baker' ?
                        <FormattedMessage id={'recruit.staffBakerMobile'} /> :
                        params.jobsNature === 'sales' ?
                        <FormattedMessage id={'recruit.staffSalesMobile'} /> :
                        params.jobsNature === 'logistics' ?
                        <FormattedMessage id={'recruit.staffLogistics'} /> :
                        params.jobsNature === 'factory' ?
                        <FormattedMessage id={'recruit.staffFactory'} /> :
                        <FormattedMessage id={'recruit.staffOfficeMobile'} />
                    }
                </span>
            </a>
            <span> {">"} </span>
            <span style={{ fontWeight: 'bold' }}>
                <FormattedMessage id={'jobs.jobApplicationForm'} />
            </span>
        </div>
        :
        <div className="jobs-nav">
            <a href={prefix + "/careers"} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    <FormattedMessage id={'jobs.selectJobNature'} />
                </span>
            </a>
            <span> {">"} </span>
            <a href={prefix + "/careers/" + params.jobsNature} style={{ color: yamazaki_brown, textDecoration: 'none' }}>
                <span>
                    <FormattedMessage id={'jobs.jobApplication'} />
                </span>
            </a>
            <span> {">"} </span>
            <span style={{ fontWeight: 'bold' }}>
                <FormattedMessage id={'jobs.jobApplicationForm'} />
            </span>
        </div>
    );

    const Form = () => {
        const [selectedAreas, setSelectedAreas] = useState([]);
        const [selectedDistricts, setSelectedDistricts] = useState([]);
        const [selectedJobLocations, setSelectedJobLocations] = useState([]);
        const [files, setFiles] = useState([]);
        const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
        const hours = Array.from({ length: 24 }, (_, i) => (i).toString().padStart(2, '0'));
        const minutes = ['00', '15', '30', '45'];

        useEffect(() => {
            handleAreaClick(firstArea);
            handleDistrictClick(firstDistrict);
        }, []);

        const handleSubmit = async (event) => {
            event.preventDefault();

            const formData = new FormData(event.target);
            const dob = formData.get('dob').split('-');

            const data = {
                job_id: jobData.id,
                name: formData.get('name'),
                birth_year: dob[0],
                birth_month: dob[1],
                phone: formData.get('phone'),
                email: formData.get('email'),
                living_district_18_code: formData.get('district'),
                available_date: formData.get('date_available'),
                job_locations: selectedJobLocations,
                how_to_know: formData.get('how_to_know'),
                remark: formData.get('remark'),
                files: files,
                available_time: []
            };

            if (jobData.type === 'PART_TIME') {
                for (let i = 1; i <= 3; i++) {
                    const start_hour = formData.get(`time_available_start_${i}_hour`);
                    const start_minute = formData.get(`time_available_start_${i}_minute`);
                    const end_hour = formData.get(`time_available_end_${i}_hour`);
                    const end_minute = formData.get(`time_available_end_${i}_minute`);
                    const weekdays = formData.getAll(`time_available_weekday_${i}`);

                    if ((start_hour && start_minute && end_hour && end_minute && weekdays.length > 0) || (start_hour === "00" && start_minute === "00" && end_hour === "00" && end_minute === "00" && weekdays.length == 0)) {
                        if (start_hour && start_minute && end_hour && end_minute && weekdays.length > 0) {
                            if (end_hour < start_hour || (end_hour === start_hour && end_minute <= start_minute)) {
                                alert(intl.formatMessage({ id: 'jobs.jobFormTimeAvailableAlert1' }));
                                return;
                            }
                            else {
                                data.available_time.push({
                                    hh_from: start_hour,
                                    mm_from: start_minute,
                                    hh_to: end_hour,
                                    mm_to: end_minute,
                                    weekday: weekdays.join(',')
                                });
                            }
                        }
                    } else {
                        alert(intl.formatMessage({ id: 'jobs.jobFormTimeAvailableAlert2' }));
                        return;
                    }
                }
            }
            else {
                delete data.available_time;
            }

            if (!data.name) {
                alert(intl.formatMessage({ id: 'jobs.jobFormNameAlert' }));
                return;
            }
            else if (!data.birth_year || !data.birth_month) {
                alert(intl.formatMessage({ id: 'jobs.jobFormDOBAlert' }));
                return;
            }
            else if (!data.phone || data.phone.length !== 8) {
                alert(intl.formatMessage({ id: 'jobs.jobFormPhoneAlert' }));
                return;
            }
            else if (!data.living_district_18_code) {
                alert(intl.formatMessage({ id: 'jobs.jobFormDistrictsAlert' }));
                return;
            }
            else if (!data.available_date) {
                alert(intl.formatMessage({ id: 'jobs.jobFormDateAvailableAlert' }));
                return;
            }
            else if (data.job_locations.length === 0) {
                alert(intl.formatMessage({ id: 'jobs.jobFormAvailableDistrictsAlert' }));
                return;
            }
            else if (!data.how_to_know) {
                alert(intl.formatMessage({ id: 'jobs.jobFormHowToKnowAlert' }));
                return;
            }
            else if (jobData.type === 'PART_TIME' && data.available_time.length === 0) {
                alert(intl.formatMessage({ id: 'jobs.jobFormTimeAvailableAlert3' }));
                return;
            }

            const result = await postRecruit(data);

            if (!result.fail) {
                alert(intl.formatMessage({ id: 'jobs.jobFormSubmitSuccess' }));
                nav(prefix + '/careers/' + params.jobsNature);
            } else {
                alert(intl.formatMessage({ id: 'jobs.jobFormSubmitFailed' }));
            }
        };

        const handleAreaClick = (area) => {
            setSelectedAreas([area]);
        };

        const handleDistrictClick = (district) => {
            setSelectedDistricts(prev => {
                if (prev.includes(district)) {
                    return prev.filter(d => d !== district);
                } else {
                    return [...prev, district];
                }
            });
        };

        const handleJobLocationChange = (jobLocationId) => {
            setSelectedJobLocations(prev => {
                if (prev.includes(jobLocationId)) {
                    return prev.filter(id => id !== jobLocationId);
                } else {
                    return [...prev, jobLocationId];
                }
            });
        };

        const handleFileChange = (event) => {
            const selectedFiles = Array.from(event.target.files);

            if (selectedFiles.length > 2) {
                alert(intl.formatMessage({ id: 'jobs.jobFormUploadFileAlert1' }));
                return;
            }
            for (let file of selectedFiles) {
                if (file.size > 10 * 1024 * 1024) {
                    alert(intl.formatMessage({ id: 'jobs.jobFormUploadFileAlert2' }));
                    return;
                }
            }
            setFiles(selectedFiles);
        };

        const ItemView = () => (
            <>
                <div className="jobs-form-row">
                    <FormattedMessage id={'jobs.jobFormShopsList'} />
                </div>
                <div className="jobs-form-row nojust wrap mobile_row">
                    {jobData?.job_locations?.map(val =>
                        val?.districts?.map(district =>
                            district.job_locations.map((job_location, index) =>
                                <label key={index} className={`jobs-form-job-location ${selectedJobLocations.includes(job_location.id) ? 'selected' : ''}`}>
                                    <input type='checkbox' name='jobs_location' value={job_location.id} onChange={() => handleJobLocationChange(job_location.id)} checked={selectedJobLocations.includes(job_location.id)} />
                                    {job_location.name}
                                </label>
                            )
                        )
                    )}
                </div>
            </>
        )

        const ListView = () => (
            <>
                <div className="jobs-form-row mobile_row" style={isMobile?{ width: '100%', alignItems: 'center' }:{ width: (jobData?.job_locations?.length * 17 + 'vw') }}>
                    <FormattedMessage id={'jobs.jobFormArea'} />
                    {jobData?.job_locations?.map((val, index) =>
                        <>
                            <div className={`jobs-form-area ${selectedAreas.includes(val.city_name) ? 'selected' : ''} ${val.districts.some(d => d.job_locations.some(j => selectedJobLocations.includes(j.id))) ? 'highlight' : ''}`} onClick={() => handleAreaClick(val.city_name)}>
                                {val.city_name}
                            </div>
                            {(index < jobData.job_locations.length - 1) ? ' | ' : ''}
                        </>
                    )}
                </div>
                <div className="jobs-form-row">
                    <FormattedMessage id={'jobs.jobFormDistrictsList'} />
                </div>
                <div className="jobs-form-row nojust wrap mobile_row">
                    {jobData?.job_locations
                        ?.filter(location => selectedAreas.includes(location.city_name))
                        ?.map(val =>
                            val?.districts.map((district, index) =>
                                <div key={index} className={`jobs-form-district ${selectedDistricts.includes(district.district_code) ? 'selected' : ''} ${district.job_locations.some(j => selectedJobLocations.includes(j.id)) ? 'highlight' : ''}`} onClick={() => handleDistrictClick(district.district_code)}>
                                    {district.district_name}
                                </div>
                            )
                        )}
                </div>
                <div className="jobs-form-row">
                    <FormattedMessage id={'jobs.jobFormShopsList'} />
                </div>
                <div className="jobs-form-row nojust wrap mobile_row">
                    {jobData?.job_locations
                        ?.filter(location => selectedAreas.includes(location.city_name))
                        ?.map(val =>
                            val?.districts?.filter(districts => selectedDistricts.includes(districts.district_code)).map(district =>
                                district.job_locations.map((job_location, index) =>
                                    <label key={index} className={`jobs-form-job-location ${selectedJobLocations.includes(job_location.id) ? 'selected' : ''}`}>
                                        <input type='checkbox' name='jobs_location' value={job_location.id} onChange={() => handleJobLocationChange(job_location.id)} checked={selectedJobLocations.includes(job_location.id)} />
                                        {job_location.name}
                                    </label>
                                )
                            )
                        )}
                </div>
            </>
        )

        return (
            <form className='jobs-form' onSubmit={handleSubmit}>
                <div className='jobs-form-title'>
                    <FormattedMessage id={'jobs.jobFormTitle'} />
                </div>
                <div className="jobs-form-row">
                    <div className='jobs-form-name'>
                        <FormattedMessage id={'jobs.jobFormName'} />*
                        <input type='text' name='name' required />
                    </div>
                    <div className='jobs-form-dob'>
                        <FormattedMessage id={'jobs.jobFormDOB'} />*
                        <input type='month' name='dob' required />
                    </div>
                    <div className='jobs-form-phone'>
                        <FormattedMessage id={'jobs.jobFormPhone'} />*
                        <input type='tel' name='phone' maxLength={8} required />
                    </div>
                </div>
                <div className="jobs-form-row">
                    <div className='jobs-form-email'>
                        <FormattedMessage id={'jobs.jobFormEmail'} />
                        <input type='email' name='email' maxLength={100} style={{ width: '30vw' }} />
                    </div>
                </div>
                <div className="jobs-form-row nojust">
                    <div className='jobs-form-districts'>
                        <FormattedMessage id={'jobs.jobFormDistricts'} />*
                        <select name='district' required>
                            {jobMetaData?.living_districts_18?.map((district, index) => (
                                <option key={index} value={district.code}>{district.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className='jobs-form-date-available'>
                        <FormattedMessage id={'jobs.jobFormDateAvailable'} />*
                        <input type='date' name='date_available' />
                    </div>
                </div>
                {jobData?.type === 'PART_TIME' &&
                    <>
                        <div className="jobs-form-row">
                            <FormattedMessage id={'jobs.jobFormTimeAvailable'} />*
                        </div>
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="jobs-form-row wrap mobile_row mobile_top_margin mobile_align_center mobile_justify_start">
                                <select name={`time_available_start_${i}_hour`} required>
                                    {hours.map(hour => (
                                        <option key={hour} value={hour}>{hour}</option>
                                    ))}
                                </select>
                                <select name={`time_available_start_${i}_minute`} required>
                                    {minutes.map(minute => (
                                        <option key={minute} value={minute}>{minute}</option>
                                    ))}
                                </select>
                                -
                                <select name={`time_available_end_${i}_hour`} required>
                                    {hours.map(hour => (
                                        <option key={hour} value={hour}>{hour}</option>
                                    ))}
                                </select>
                                <select name={`time_available_end_${i}_minute`} required>
                                    {minutes.map(minute => (
                                        <option key={minute} value={minute}>{minute}</option>
                                    ))}
                                </select>
                                {isMobile && <div style={{width:"100%"}}/>}
                                {weekdays.map((day, index) => (
                                    <label key={index}>
                                        <input type="checkbox" name={`time_available_weekday_${i}`} value={day} />
                                        <FormattedMessage id={`jobs.jobForm${day}`} />
                                    </label>
                                ))}
                            </div>
                        ))}
                    </>
                }
                <div className="jobs-form-row mobile_top_margin">
                    <FormattedMessage id={'jobs.jobFormAvailableDistricts'} />*
                </div>
                {
                    jobData?.job_locations_view === 'LIST_VIEW' ?
                        <ListView />
                        :
                        <ItemView />
                }
                <div className="jobs-form-row mobile_top_margin">
                    <FormattedMessage id={'jobs.jobFormHowToKnow'} />
                </div>
                <div className="jobs-form-row radio nojust wrap mobile_row">
                    {jobMetaData?.how_to_know?.map((option, index) => (
                        <label key={index} style={{ display: 'flex', alignItems: 'center' }}>
                            <input type="radio" name="how_to_know" value={option.code} />
                            <span>{option.name}</span>
                        </label>
                    ))}
                </div>
                <div className="jobs-form-row mobile_top_margin">
                    <FormattedMessage id={'jobs.jobFormRemark'} />
                    <input type='text' name='remark' style={{ flex: 1, marginLeft: '1vw' }} />
                </div>
                <div className="jobs-form-row">
                    <label className='jobs-form-upload'>
                        <FormattedMessage id={'jobs.jobFormUploadResume'} />
                        <input id="uploads_button" name="uploads_button" type='file' onChange={handleFileChange} multiple hidden />
                    </label>
                    <div className="uploaded-files">
                        {files.map((file, index) => (
                            <div key={index}>
                                {file.name}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="jobs-form-row mobile_top_margin">
                    <FormattedMessage id={'jobs.jobFormUploadRemark'} />
                </div>
                <div className="jobs-privacy-title">
                    <FormattedMessage id={'jobs.jobFormPrivaryTitle'} />
                </div>
                <div className="jobs-privacy-terms">
                    <span style={{ width: '2vw' }}>1.</span>
                    <span style={{ flex: 1 }}>
                        <FormattedMessage id={'jobs.jobFormPrivacyTerms1'} />
                    </span>
                </div>
                <div className="jobs-privacy-terms">
                    <span style={{ width: '2vw' }}>2.</span>
                    <span style={{ flex: 1 }}>
                        <FormattedMessage id={'jobs.jobFormPrivacyTerms2'} />
                    </span>
                </div>
                <div className="jobs-privacy-terms">
                    <span style={{ width: '2vw' }}>3.</span>
                    <span style={{ flex: 1 }}>
                        <FormattedMessage
                            id={'jobs.jobFormPrivacyTerms3'}
                            values={{
                                enquiry_email: <a href='mailto:enquiry@yamazaki.hk'>enquiry@yamazaki.hk</a>
                            }}
                        />
                    </span>
                </div>
                <div className="jobs-form-row center">
                    <button type="submit" className="job-form-submit">
                        <FormattedMessage id={'jobs.jobFormSubmit'} />
                    </button>
                </div>
            </form>
        )
    }

    return (
        <div className="jobs-application">
            <div className="jobs-background">
                <img src={prefix + '/images/recruit_background.png'} />
                <span className="jobs-title" style={{ letterSpacing: isEng?'0':'1.5vw' }}>
                    <FormattedMessage id={'jobs.title'} />
                </span>
            </div>
            <NavBar />
            <div className='jobs-content-background' />
            <Form />
        </div>
    );
};

export default JobsApplication;